import { Button, ButtonColor, ButtonSize } from '@code/innovance-react-ui-kit';

import React, { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { DocumentStorage, PlasticCardRegistrationApi } from 'src/front/api/apiInstance';
import { PlasticCardAccountsSearchData } from 'src/front/api/generated';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import displayPDF from 'src/front/common/utils/domUtils';
import { PeriodSchema } from 'src/front/common/utils/validationSchemas';
import Datepicker from 'src/front/components/Datepicker';
import { FormElement, FormRow, Separator, Subtitle } from 'src/front/components/FormStyles';
import { useUserInfoState } from 'src/front/contexts/SessionContext';

interface Props {
  selectedAccountsRow: PlasticCardAccountsSearchData;
}

export interface AccountsPeriodFormData {
  beginDate: Date | null;
  endDate: Date | null;
}

const defaultValues = {
  beginDate: null,
  endDate: null,
};

const AccountsPeriod: FC<Props> = ({ selectedAccountsRow }) => {
  const { t } = useTranslation();
  const { processDate } = useUserInfoState();
  const defaultProcessDate = processDate || null;
  const resolver = useYupResolver(PeriodSchema, { todayMaxControl: true });

  const { register, errors, handleSubmit, control, getValues } = useForm<AccountsPeriodFormData>({
    defaultValues: {
      ...defaultValues,
      beginDate: defaultProcessDate,
      endDate: defaultProcessDate,
    },
    resolver,
  });

  useEffect(() => {
    register({ name: 'beginDate' });
    register({ name: 'endDate' });
  }, [register]);

  const onSubmit = async (): Promise<void> => {
    const { beginDate, endDate } = getValues();
    const { data } = await PlasticCardRegistrationApi.generatePlasticCardAccountDocument({
      documentNumber: selectedAccountsRow.documentNumber,
      accountName: selectedAccountsRow.accountName,
      startDate: formatDate(beginDate),
      endDate: formatDate(endDate),
    });

    if (data.result === 'OK') {
      const { data: response } = await DocumentStorage.getDocument({
        documentId: data.documentId,
      });

      if (response.result === 'OK') {
        displayPDF(response.documentData);
      }
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Subtitle>
          <Trans
            i18nKey={`${selectedAccountsRow.documentNumber} - ${selectedAccountsRow.accountName}`}
          />
        </Subtitle>
        <Separator />
        <Subtitle>
          <Trans i18nKey="general_components_period_label" />
        </Subtitle>
        <Separator />
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="beginDate"
              render={({ onChange, value }) => (
                <Datepicker
                  errorMessage={t(errors?.beginDate?.message as string)}
                  inputLabel={`${t('general_components_startDate_label')} *`}
                  onChange={onChange}
                  value={value as Date}
                />
              )}
            />
          </FormElement>

          <FormElement>
            <Controller
              control={control}
              name="endDate"
              render={({ onChange, value }) => (
                <Datepicker
                  errorMessage={t(errors?.endDate?.message as string)}
                  inputLabel={`${t('general_components_endDate_label')} *`}
                  onChange={onChange}
                  value={value as Date}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <FormElement>
          <Button
            color={ButtonColor.Primary}
            size={ButtonSize.Medium}
            text={t('plasticCardRegistration_accountStatement_button')}
            type="submit"
          />
        </FormElement>
        <Separator />
      </form>
    </>
  );
};

export default AccountsPeriod;
